/**
 * 🛥️ CREATE YACHT BOOKING DIALOG
 * Dialog for creating yacht charter bookings
 * Supports both destination-based trips and time-based charters
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  Stack,
  Chip,
  RadioGroup,
  Radio,
  Card,
  CardContent,
} from '@mui/material';
import { DirectionsBoat, Place, CalendarToday, LocationOn, Restaurant, CheckCircle, Assignment, Person, Business } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import CustomerSelector from './CustomerSelector';
import GuestCustomerDialog from './GuestCustomerDialog';
import YachtSelector from './YachtSelector';
import BookingCostPreview from './BookingCostPreview';
import adminBookingService, {
  YachtCharterBookingRequest,
  CalculateCostsRequest,
  CostCalculation,
} from '@/services/adminBookingService';
import { yachtApprovalService, PendingYacht } from '@/services/yachtApprovalService';
import { yachtDestinationsService, YachtDestination } from '@/services/yachtDestinationsService';
import { yachtDestinationPricingService, YachtDestinationPricing } from '@/services/yachtDestinationPricingService';
import { User } from '@/services/userService';
import { paystackPaymentService } from '@/services/paystackPaymentService';
import { CorporateAccountSelector } from '@/components/corporate/CorporateAccountSelector';
import { CorporateAccount } from '@/services/corporateAccountsService';
import {
  validateCorporateBooking,
  validateBookingCustomer,
} from '@/utils/corporateBookingValidation';

interface CreateYachtBookingDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateYachtBookingDialog: React.FC<CreateYachtBookingDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  // Booking type
  const [bookingType, setBookingType] = useState<'destination' | 'time-based'>('time-based');

  // Booking customer type (Individual vs Corporate)
  const [bookingCustomerType, setBookingCustomerType] = useState<'individual' | 'corporate'>('individual');

  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [guestCustomerDialogOpen, setGuestCustomerDialogOpen] = useState(false);

  // Corporate Account
  const [corporateAccountId, setCorporateAccountId] = useState<string | null>(null);
  const [selectedCorporateAccount, setSelectedCorporateAccount] = useState<CorporateAccount | null>(null);
  const [hasSufficientCredit, setHasSufficientCredit] = useState(true);

  // Yacht selection
  const [selectedYacht, setSelectedYacht] = useState<PendingYacht | null>(null);

  // Destination selection (for destination-based trips - NOT YET SUPPORTED BY BACKEND)
  const [destinations, setDestinations] = useState<YachtDestination[]>([]);
  const [yachtDestinationPricings, setYachtDestinationPricings] = useState<YachtDestinationPricing[]>([]);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<string>('');
  const [tripType, setTripType] = useState<'one_way' | 'round_trip'>('round_trip');

  // Rental option (time-based charters - CURRENTLY SUPPORTED)
  const [rentalOption, setRentalOption] = useState<'hourly' | 'half_day' | 'full_day' | 'weekly'>('full_day');

  // Date/Time
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('10:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('18:00');
  const [pickupTime, setPickupTime] = useState('18:00'); // For round trip destination bookings
  const [hasPastDate, setHasPastDate] = useState(false);
  const [showPastDateConfirmation, setShowPastDateConfirmation] = useState(false);

  // Location
  const [pickupLocation, setPickupLocation] = useState('');

  // Add-ons
  const [catering, setCatering] = useState(false);
  const [cateringPreferences, setCateringPreferences] = useState('');
  const [cateringCost, setCateringCost] = useState(0);

  // Payment
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid'>('pending');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentOption, setPaymentOption] = useState<'send-link' | 'pay-now'>('send-link');
  const [paymentLinkDialog, setPaymentLinkDialog] = useState({ open: false, link: '', bookingId: '', email: '' });

  // Admin overrides
  const [adminNotes, setAdminNotes] = useState('');
  const [skipAvailabilityCheck, setSkipAvailabilityCheck] = useState(false);

  // Cost calculation
  const [costCalculation, setCostCalculation] = useState<CostCalculation | null>(null);
  const [calculatingCost, setCalculatingCost] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  // Submission
  const [submitting, setSubmitting] = useState(false);

  // Load yacht-specific destinations when yacht is selected (for destination-based trips)
  useEffect(() => {
    if (open && bookingType === 'destination' && selectedYacht) {
      loadYachtDestinations();
    } else if (bookingType === 'destination' && !selectedYacht) {
      // Clear destinations if no yacht selected
      setDestinations([]);
      setYachtDestinationPricings([]);
      setSelectedDestination('');
    }
  }, [open, bookingType, selectedYacht]);

  // Calculate costs when relevant fields change
  useEffect(() => {
    if (shouldCalculateCost()) {
      calculateCosts();
    }
  }, [
    selectedCustomer,
    corporateAccountId,
    selectedYacht,
    selectedDestination,
    tripType,
    rentalOption,
    startDate,
    endDate,
    catering,
    cateringCost,
  ]);

  // Check if start date is in the past
  useEffect(() => {
    if (!startDate) {
      setHasPastDate(false);
      return;
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for fair comparison

    const selectedDate = new Date(startDate);
    const isPastDate = selectedDate < now;

    setHasPastDate(isPastDate);
  }, [startDate]);

  const loadYachtDestinations = async () => {
    if (!selectedYacht) return;

    setLoadingDestinations(true);
    setDestinations([]);
    setYachtDestinationPricings([]);
    setSelectedDestination('');

    try {
      // Fetch destinations that this specific yacht can service
      const response = await yachtDestinationPricingService.getYachtPricing(selectedYacht.id);


      if (response.success && response.pricings) {
        // Filter only available destinations
        const availablePricings = response.pricings.filter(p => p.isAvailable);

        setYachtDestinationPricings(availablePricings);

        // Extract destination info from pricings
        const yachtDestinations: YachtDestination[] = availablePricings
          .filter(p => p.destination)
          .map(p => ({
            id: p.destinationId,
            destinationName: p.destination!.destinationName,
            country: p.destination!.country,
            city: p.destination!.city,
            description: p.destination!.description || '',
            location: {
              coordinates: { latitude: 0, longitude: 0 },
              address: '',
              landmark: '',
              estimatedDistanceKm: p.destination!.estimatedDistanceKm || 0,
            },
            isActive: p.isAvailable,
            createdAt: p.createdAt,
            updatedAt: p.updatedAt,
          }));

        setDestinations(yachtDestinations);

        if (yachtDestinations.length === 0) {
          toast.error(`${selectedYacht.yacht.name} doesn't have any destination pricing set up. Please use time-based charter.`);
        }
      } else {
        toast.error('Failed to load destinations for this yacht');
      }
    } catch (error) {
      console.error('Error loading yacht destinations:', error);
      toast.error('Failed to load yacht destinations');
    } finally {
      setLoadingDestinations(false);
    }
  };

  const shouldCalculateCost = (): boolean => {
    // Must have either individual customer or corporate account
    const hasCustomer = bookingCustomerType === 'individual' ? !!selectedCustomer : !!corporateAccountId;
    if (!hasCustomer || !selectedYacht || !startDate) return false;

    if (bookingType === 'destination') {
      // Need to have a destination selected AND have the pricing record for it
      if (!selectedDestination) return false;
      const pricingRecord = yachtDestinationPricings.find((p) => p.destinationId === selectedDestination);
      return !!pricingRecord;
    } else {
      // Time-based charter: just need customer, yacht, and startDate
      // rentalOption determines the pricing, not duration
      return true;
    }
  };

  const calculateCosts = async () => {
    // Need either customer or corporate account for cost calculation
    const hasCustomer = bookingCustomerType === 'individual' ? !!selectedCustomer : !!corporateAccountId;
    if (!hasCustomer || !selectedYacht) return;

    setCalculatingCost(true);
    setCostError(null);

    try {
      const request: CalculateCostsRequest = {
        // Send EITHER customerId OR corporateAccountId (not both)
        customerId: bookingCustomerType === 'individual' ? selectedCustomer?.id : undefined,
        corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
        yachtId: selectedYacht.id,
        startDate: `${startDate}T${startTime}:00Z`,
        serviceCategory: 'yacht-charter',
      };

      if (bookingType === 'destination') {
        // For destination-based trips
        // Find the yacht-destination-pricing record for this destination
        const pricingRecord = yachtDestinationPricings.find((p) => p.destinationId === selectedDestination);
        if (pricingRecord) {
          // Send the yacht-destination-pricing ID, not just the destination ID
          request.destinationPricingId = pricingRecord.id;
          request.tripType = tripType;
        }
      } else {
        // For time-based yacht charter (CURRENTLY SUPPORTED)
        // Yacht pricing is based on rentalOption (hourly/half_day/full_day/weekly)
        // endDate is same as startDate since pricing is not duration-based
        request.rentalOption = rentalOption;
        request.endDate = `${startDate}T${startTime}:00Z`; // Same as startDate for hourly-based charter
      }

      const response = await adminBookingService.calculateCosts(request);

      if (response.success && response.data) {
        setCostCalculation(response.data);
      } else {
        console.error('❌ Cost calculation error:', response.error);
        setCostError(
          typeof response.error === 'string'
            ? response.error
            : response.error?.message || 'Failed to calculate costs'
        );
        setCostCalculation(null);
      }
    } catch (error) {
      console.error('Error calculating costs:', error);
      setCostError('Failed to calculate costs');
      setCostCalculation(null);
    } finally {
      setCalculatingCost(false);
    }
  };

  const handleSubmit = async () => {
    // Validate customer/corporate account selection
    const customerValidation = validateBookingCustomer(
      selectedCustomer?.id,
      corporateAccountId || undefined
    );
    if (!customerValidation.isValid) {
      toast.error(customerValidation.error || 'Please select a customer or corporate account');
      return;
    }

    // Validate corporate booking if applicable
    if (bookingCustomerType === 'corporate' && corporateAccountId) {
      const validation = validateCorporateBooking(
        selectedCorporateAccount,
        costCalculation?.totalCost || 0,
        true
      );

      if (!validation.isValid) {
        toast.error(validation.errors.join(', '));
        return;
      }

      // Show warnings
      validation.warnings.forEach(warning => toast.warning(warning));
    }
    if (!selectedYacht) {
      toast.error('Please select a yacht');
      return;
    }
    if (!startDate) {
      toast.error('Please select start date');
      return;
    }
    if (bookingType === 'destination') {
      if (!selectedDestination) {
        toast.error('Please select a destination');
        return;
      }
      // Verify we have the pricing record for this destination
      const pricingRecord = yachtDestinationPricings.find((p) => p.destinationId === selectedDestination);
      if (!pricingRecord) {
        toast.error('Pricing information not found for selected destination');
        return;
      }
    }
    if (!pickupLocation) {
      toast.error('Please enter pickup location');
      return;
    }
    if (!costCalculation) {
      toast.error('Cost calculation failed. Please check your inputs.');
      return;
    }

    // Check for past date and show confirmation
    if (hasPastDate) {
      setShowPastDateConfirmation(true);
      return;
    }

    await performSubmit();
  };

  const performSubmit = async () => {
    setSubmitting(true);

    try {
      // Extract baseCost from cost breakdown (the rental cost)
      const baseCost = costCalculation.costBreakdown.find((item) => item.type === 'rental')?.amount || 0;
      const securityDeposit = costCalculation.costBreakdown.find((item) => item.type === 'deposit')?.amount;

      const request: YachtCharterBookingRequest = {
        customerId: bookingCustomerType === 'individual' ? selectedCustomer?.id : undefined,
        corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
        yachtId: selectedYacht.id,
        startDate: `${startDate}T${startTime}:00Z`,
        // For time-based charter: endDate is same as startDate (pricing is hourly-based via rentalOption)
        // For destination-based: endDate is same as startDate
        endDate: `${startDate}T${startTime}:00Z`,
        pickupLocation,
        baseCost, // Required by API contract - amounts are in Naira
        totalAmount: costCalculation.totalCost,
        catering,
        cateringPreferences: catering ? cateringPreferences : undefined,
        cateringCost: catering ? cateringCost : undefined,
        securityDeposit,
        paymentStatus,
        paymentMethod: paymentMethod || undefined,
        adminNotes: adminNotes || undefined,
        skipAvailabilityCheck,
      };

      if (bookingType === 'destination') {
        // Find the yacht-destination-pricing record for this destination
        const pricingRecord = yachtDestinationPricings.find((p) => p.destinationId === selectedDestination);
        if (pricingRecord) {
          // Send the yacht-destination-pricing ID, not just the destination ID
          request.destinationPricingId = pricingRecord.id;
          request.tripType = tripType;
          if (tripType === 'round_trip') {
            request.pickupTime = pickupTime;
          }
        }
      } else {
        // Time-based charter (REQUIRED)
        request.rentalOption = rentalOption;
      }

      const response = await adminBookingService.createYachtCharterBooking(request);

      if (response.success) {
        const createdBookingId = response.data?.id;

        // If payment option is 'send-link', generate payment link
        if (paymentOption === 'send-link') {

          // Get customer email - for corporate bookings, use contact person email
          const customerEmail = bookingCustomerType === 'individual'
            ? (selectedCustomer?.email || selectedCustomer?.personalInfo?.email || '')
            : (selectedCorporateAccount?.contactPerson?.email || '');

          const paymentResponse = await paystackPaymentService.initializePayment({
            email: customerEmail,
            amount: costCalculation.totalCost,
            bookingId: createdBookingId,
            metadata: {
              bookingType: 'yacht-charter',
              corporateAccountId: bookingCustomerType === 'corporate' ? corporateAccountId || undefined : undefined,
              yachtName: selectedYacht.yacht.name,
              customerName: bookingCustomerType === 'individual'
                ? (selectedCustomer?.name || selectedCustomer?.personalInfo?.fullName || '')
                : (selectedCorporateAccount?.contactPerson?.name || selectedCorporateAccount?.companyName || ''),
            },
          });

          if (paymentResponse.success) {
            const paymentLink = paymentResponse.data.authorizationUrl || paymentResponse.data.authorization_url;


            // Show payment link dialog
            setPaymentLinkDialog({
              open: true,
              link: paymentLink,
              bookingId: createdBookingId,
              email: selectedCustomer.email,
            });

            toast.success('Booking created! Payment link generated.');
            // Don't close the main dialog yet - wait for user to close payment link dialog
          } else {
            toast.error('Booking created but failed to generate payment link');
            // Even if payment link fails, booking was created successfully
            onSuccess();
            handleClose();
          }
        } else {
          // For 'pay-now', process payment immediately
          toast.success(
            `Yacht charter booking created successfully! Booking #${response.data?.bookingNumber || ''}`
          );
          onSuccess();
          handleClose();
        }
      } else {
        const errorMessage =
          typeof response.error === 'string'
            ? response.error
            : response.error?.message || 'Failed to create booking';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating yacht booking:', error);
      toast.error('Failed to create yacht booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setBookingType('time-based');
    setBookingCustomerType('individual');
    setSelectedCustomer(null);
    setCorporateAccountId(null);
    setSelectedCorporateAccount(null);
    setHasSufficientCredit(true);
    setSelectedYacht(null);
    setDestinations([]);
    setYachtDestinationPricings([]);
    setSelectedDestination('');
    setTripType('round_trip');
    setRentalOption('full_day');
    setStartDate('');
    setStartTime('10:00');
    setEndDate('');
    setEndTime('18:00');
    setPickupTime('18:00');
    setPickupLocation('');
    setCatering(false);
    setCateringPreferences('');
    setCateringCost(0);
    setPaymentStatus('pending');
    setPaymentMethod('');
    setPaymentOption('send-link');
    setAdminNotes('');
    setSkipAvailabilityCheck(false);
    setCostCalculation(null);
    setCostError(null);
    onClose();
  };

  const selectedDestinationData = destinations.find((d) => d.id === selectedDestination);

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <DirectionsBoat />
          <Typography variant="h6">Create Yacht Charter Booking</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          {/* Booking Type Tabs */}
          <Tabs
            value={bookingType}
            onChange={(_, value) => setBookingType(value)}
            sx={{ mb: 3 }}
          >
            <Tab
              icon={<DirectionsBoat />}
              label="Time-Based Charter"
              value="time-based"
              iconPosition="start"
            />
            <Tab
              icon={<Place />}
              label="Destination Trip"
              value="destination"
              iconPosition="start"
            />
          </Tabs>

          <Grid container spacing={3}>
            {/* Customer Type Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                1. Who is this booking for?
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <RadioGroup
                    value={bookingCustomerType}
                    onChange={(e) => {
                      const newType = e.target.value as 'individual' | 'corporate';
                      setBookingCustomerType(newType);
                      // Clear selections when switching
                      if (newType === 'individual') {
                        setCorporateAccountId(null);
                        setSelectedCorporateAccount(null);
                      } else {
                        setSelectedCustomer(null);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Individual Customer
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Regular customer booking
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: bookingCustomerType === 'individual' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                        mb: 1,
                      }}
                    />
                    <FormControlLabel
                      value="corporate"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Business />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              Corporate Account
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Business account with credit limit
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: bookingCustomerType === 'corporate' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    />
                  </RadioGroup>
                </CardContent>
              </Card>
            </Grid>

            {/* Individual Customer Selection */}
            {bookingCustomerType === 'individual' && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  2. Select Customer
                </Typography>
                <CustomerSelector
                  selectedCustomer={selectedCustomer}
                  onSelectCustomer={setSelectedCustomer}
                onCreateNew={() => setGuestCustomerDialogOpen(true)}
                required
              />
            </Grid>
            )}

            {/* Corporate Account Selection */}
            {bookingCustomerType === 'corporate' && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  2. Select Corporate Account
                </Typography>
                <CorporateAccountSelector
                  value={corporateAccountId}
                  onChange={(accountId, account) => {
                    setCorporateAccountId(accountId);
                    setSelectedCorporateAccount(account);
                  }}
                  bookingAmount={costCalculation?.totalCost || 0}
                  onCreditValidation={(hasCredit) => {
                    setHasSufficientCredit(hasCredit);
                  }}
                  disabled={submitting}
                />
              </Grid>
            )}

            {/* Destination Selection (for destination-based trips) */}
            {bookingType === 'destination' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                    3. Select Destination
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth required>
                    <InputLabel>Destination</InputLabel>
                    <Select
                      value={selectedDestination}
                      label="Destination"
                      onChange={(e) => setSelectedDestination(e.target.value)}
                      disabled={loadingDestinations || !selectedYacht || destinations.length === 0}
                    >
                      {!selectedYacht && (
                        <MenuItem disabled>
                          Please select a yacht first
                        </MenuItem>
                      )}
                      {selectedYacht && loadingDestinations && (
                        <MenuItem disabled>
                          Loading destinations for {selectedYacht.yacht.name}...
                        </MenuItem>
                      )}
                      {selectedYacht && !loadingDestinations && destinations.length === 0 && (
                        <MenuItem disabled>
                          {selectedYacht.yacht.name} has no available destinations
                        </MenuItem>
                      )}
                      {selectedYacht && !loadingDestinations && destinations.map((dest) => (
                        <MenuItem key={dest.id} value={dest.id}>
                          {dest.destinationName} - {dest.city}, {dest.country}
                          {dest.location.estimatedDistanceKm && ` (${dest.location.estimatedDistanceKm} km)`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedYacht && !loadingDestinations && destinations.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      {selectedYacht.yacht.name} doesn't have destination pricing configured.
                      Please switch to "Time-Based Charter" to book this yacht.
                    </Alert>
                  )}
                  {selectedDestinationData && selectedDestinationData.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {selectedDestinationData.description}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Trip Type</InputLabel>
                    <Select
                      value={tripType}
                      label="Trip Type"
                      onChange={(e: any) => setTripType(e.target.value)}
                    >
                      <MenuItem value="one_way">One-Way</MenuItem>
                      <MenuItem value="round_trip">Round-Trip</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Rental Option (for time-based charters) */}
            {bookingType === 'time-based' && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                  3. Charter Duration
                </Typography>
                <FormControl fullWidth required>
                  <InputLabel>Charter Option</InputLabel>
                  <Select
                    value={rentalOption}
                    label="Charter Option"
                    onChange={(e: any) => setRentalOption(e.target.value)}
                  >
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="half_day">Half Day</MenuItem>
                    <MenuItem value="full_day">Full Day</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Date & Time */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                4. Date & Time
              </Typography>
            </Grid>
            {hasPastDate && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  ⚠️ <strong>Past Date Selected:</strong> You are creating a booking with a start date in the past.
                  This should only be done for historical records or special circumstances. Please confirm this is intentional.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={bookingType === 'time-based' ? 6 : 12}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Stack>
            </Grid>
            {bookingType === 'time-based' && (
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                  <TextField
                    fullWidth
                    type="time"
                    label="End Time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Stack>
              </Grid>
            )}
            {bookingType === 'destination' && tripType === 'round_trip' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Return Pickup Time"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Time to pick up passengers for return journey"
                />
              </Grid>
            )}

            {/* Location */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                5. Pickup Location
              </Typography>
              <TextField
                fullWidth
                label="Pickup Location"
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
                placeholder="e.g., Dubai Marina, Lagos Marina"
              />
            </Grid>

            {/* Yacht Selection */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                6. Select Yacht
              </Typography>
              <YachtSelector
                selectedYacht={selectedYacht}
                onSelectYacht={setSelectedYacht}
                required
              />
            </Grid>

            {/* Add-ons */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                7. Add-ons
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch checked={catering} onChange={(e) => setCatering(e.target.checked)} />
                }
                label="Add Catering Service"
              />
              {catering && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Catering Preferences"
                      value={cateringPreferences}
                      onChange={(e) => setCateringPreferences(e.target.value)}
                      multiline
                      rows={2}
                      placeholder="e.g., Vegetarian menu for 8 guests"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Restaurant />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Catering Cost"
                      value={cateringCost}
                      onChange={(e) => setCateringCost(parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>

            {/* Payment */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                8. Payment
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentStatus}
                  label="Payment Status"
                  onChange={(e: any) => setPaymentStatus(e.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Method (Optional)"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="e.g., Card, Cash, Bank Transfer"
              />
            </Grid>

            {/* Payment Option */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    How to process payment?
                  </Typography>
                  <RadioGroup
                    value={paymentOption}
                    onChange={(e) => setPaymentOption(e.target.value as 'send-link' | 'pay-now')}
                  >
                    <FormControlLabel
                      value="send-link"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Send Payment Link
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Create booking and email payment link to customer
                          </Typography>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: paymentOption === 'send-link' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                        mb: 1,
                      }}
                    />
                    <FormControlLabel
                      value="pay-now"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Process Payment Now
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Open Paystack popup for immediate payment
                          </Typography>
                        </Box>
                      }
                      sx={{
                        p: 2,
                        border: paymentOption === 'pay-now' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    />
                  </RadioGroup>
                </CardContent>
              </Card>
            </Grid>

            {/* Admin Options */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                9. Admin Options
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                multiline
                rows={2}
                placeholder="Internal notes about this booking..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={skipAvailabilityCheck}
                    onChange={(e) => setSkipAvailabilityCheck(e.target.checked)}
                  />
                }
                label="Skip Availability Check (Override conflicts)"
              />
            </Grid>

            {/* Cost Preview */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Cost Preview
              </Typography>
              <BookingCostPreview
                calculation={costCalculation}
                loading={calculatingCost}
                error={costError}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            submitting ||
            !costCalculation ||
            (bookingCustomerType === 'corporate' && !hasSufficientCredit)
          }
          startIcon={submitting && <CircularProgress size={16} />}
        >
          {submitting
            ? (paymentOption === 'send-link' ? 'Sending Link...' : 'Creating...')
            : (paymentOption === 'send-link' ? 'Create Booking & Send Link' : 'Create Booking')
          }
        </Button>
      </DialogActions>

      {/* Payment Link Dialog */}
      <Dialog
        open={paymentLinkDialog.open}
        onClose={() => {
          setPaymentLinkDialog({ open: false, link: '', bookingId: '', email: '' });
          onSuccess(); // Refresh the bookings list
          handleClose(); // Close the main booking dialog
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle />
            <Typography variant="h6">Booking Created Successfully!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Booking created with ID: <strong>{paymentLinkDialog.bookingId}</strong>
          </Alert>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Payment Link for {paymentLinkDialog.email}:
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              mb: 2,
              wordBreak: 'break-all'
            }}
          >
            <Typography
              variant="body2"
              component="a"
              href={paymentLinkDialog.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {paymentLinkDialog.link}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              navigator.clipboard.writeText(paymentLinkDialog.link);
              toast.success('Payment link copied to clipboard!');
            }}
            startIcon={<Assignment />}
          >
            Copy Link to Clipboard
          </Button>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Share this link with the customer via WhatsApp, Email, or SMS. They can click it to complete payment.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPaymentLinkDialog({ open: false, link: '', bookingId: '', email: '' });
              onSuccess(); // Refresh the bookings list
              handleClose(); // Close the main booking dialog
            }}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>

    {/* Guest Customer Dialog */}
    <GuestCustomerDialog
      open={guestCustomerDialogOpen}
      onClose={() => setGuestCustomerDialogOpen(false)}
      onConfirm={(guestCustomer) => {
        setSelectedCustomer(guestCustomer);
        setGuestCustomerDialogOpen(false);
      }}
    />

    {/* Past Date Confirmation Dialog */}
    <Dialog
      open={showPastDateConfirmation}
      onClose={() => setShowPastDateConfirmation(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ⚠️ Confirm Past Date Booking
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You are about to create a booking with a start date in the past.
        </Alert>
        <Typography variant="body1" gutterBottom>
          <strong>Start Date:</strong> {startDate}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Past date bookings should only be created for:
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 2 }}>
          <li>Historical record-keeping</li>
          <li>Data migration purposes</li>
          <li>Backdating approved bookings</li>
        </Box>
        <Typography variant="body2" color="error">
          Are you sure you want to proceed with this past date booking?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowPastDateConfirmation(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => {
            setShowPastDateConfirmation(false);
            performSubmit();
          }}
          disabled={submitting}
        >
          {submitting ? 'Creating...' : 'Yes, Create Past Booking'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CreateYachtBookingDialog;
